import { Suspense, lazy, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { HOME_ROUTE_FOR_ROLE } from '../../routes/roleHome';
import './HomePage.css';

/**
 * The rotating hero object, split into its own chunk.
 *
 * React.lazy is doing real work here rather than being ceremony: the import only fires
 * when the component is actually rendered, so a phone never requests the chunk. Gating it
 * with CSS instead would have shipped and parsed the same bytes to every mobile visitor
 * and, on some browsers, kept compositing an animation they cannot see.
 */
const HeroObject = lazy(() => import('../../components/HeroObject'));

/** The breakpoint at which the hero has room for a second column. */
const DESKTOP = '(min-width: 1024px)';

/**
 * Track a media query, and re-evaluate when it changes.
 *
 * Reads the match during the first render rather than defaulting to false and correcting
 * in an effect, so a desktop visitor does not get a frame of hero without the object and
 * a reflow immediately after.
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );

  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * The public landing page.
 *
 * CUSTOMER AND INVESTOR FACING. Nothing here may reference build state, task
 * numbers, engine endpoints, or connectivity: this is the first thing a ticket buyer
 * or an accredited investor sees.
 *
 * COPY DISCIPLINE, because two of the three claims below are regulated or easy to
 * overstate:
 *
 *   Zero fees is scoped to CHECKOUT and nowhere else. The engine computes an order
 *   total as exactly the sum of ticket face values, so that claim is literally true at
 *   checkout. It is NOT true of refunds on cancelled events, which have fees deducted
 *   per the T&Cs, so the copy stays silent on refunds rather than implying a promise
 *   that does not hold.
 *
 *   Reliability scores are only shown once an artist has five completed events, so the
 *   copy says the score is built from completed bookings rather than implying every
 *   artist carries one.
 *
 *   The investing pointer and the five role cards moved to the site footer in 49b. They
 *   served a small fraction of the people who land here and made a ticketing site read
 *   as a multi-audience pitch. Keeping a securities offering out of the homepage body is
 *   also the right side of the line 506(c) already draws.
 */

const PILLARS = [
  {
    title: 'No fees at checkout',
    body: 'The ticket price is the total. Voleto adds no booking fee and no service fee '
      + 'when you buy, so what you see on the event page is what you pay.',
  },
  {
    title: 'Artists with a track record',
    body: 'Reliability is built from completed bookings, not self-reported. Once an artist '
      + 'has enough performances on the platform, organizers see a score drawn from what '
      + 'actually happened.',
  },
  {
    // The card title carried the same word the rest of the product has been purging, so
    // it goes with the body rather than being left as the last one standing.
    title: 'Invest in real productions',
    body: 'Live entertainment productions powered by Voleto raise capital from accredited '
      + 'investors under Regulation D, managed by Vyrtual LLC. Open offerings, their terms '
      + 'and the disclosures that go with them are published in the marketplace.',
  },
];


export function HomePage() {
  const { user, isAuthenticated } = useAuth();
  const isDesktop = useMediaQuery(DESKTOP);

  return (
    <div className="home">
      <section className="home__hero">
        <div className="home__hero-copy">
          <h1 className="home__title">
            The operating system for{' '}
            <span className="home__accent">live entertainment</span>
          </h1>
          <p className="home__lede">
            Voleto runs the whole show, ticketing without fees, booking with a real
            performance record behind it, and investment in the productions themselves.
          </p>

          <div className="home__cta">
            <Link to="/events" className="button button--primary">
              Browse events
            </Link>
            <Link to="/invest" className="button button--ghost">
              Investment offerings
            </Link>
          </div>
        </div>

        {/* No fallback content while the chunk loads. It is decorative, so a brief gap is
            preferable to a placeholder box that shifts the layout when it resolves. */}
        {isDesktop && (
          <Suspense fallback={null}>
            <HeroObject />
          </Suspense>
        )}
      </section>

      <section className="home__pillars" aria-labelledby="pillars-heading">
        <h2 id="pillars-heading" className="home__section-title">
          What Voleto does differently
        </h2>
        <ul className="home__pillar-grid">
          {PILLARS.map((pillar) => (
            <li key={pillar.title} className="home__pillar glass-card">
              <h3 className="home__pillar-title">{pillar.title}</h3>
              <p className="home__pillar-body">{pillar.body}</p>
            </li>
          ))}
        </ul>
      </section>



      <section className="home__signup glass-panel glass-edge">
        {isAuthenticated && user ? (
          <>
            <h2 className="home__signup-title">Welcome back</h2>
            <Link to={HOME_ROUTE_FOR_ROLE[user.role]} className="button button--primary">
              Go to your workspace
            </Link>
          </>
        ) : (
          <>
            <h2 className="home__signup-title">Get started</h2>
            <p className="home__section-lede">
              One account covers buying tickets, running events, performing, and investing.
            </p>
            <div className="home__cta">
              <Link to="/register" className="button button--primary">
                Create an account
              </Link>
              <Link to="/login" className="button button--ghost">
                Sign in
              </Link>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
